import React, { forwardRef, useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { ATTACHMENT_ACCEPT, MAX_FILES_PER_SEND } from '../utils/attachments';
import AttachmentTray from './AttachmentTray';

const Container = styled.div`
  padding: 10px 14px 12px;
  background: white;
  border-top: 1px solid #e2e8f0;
  position: relative;
`;

// One pill holds the text and the send button; the outline turns navy only while typing
const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  position: relative;
  padding: ${props => (props.$withAttach ? '5px 5px 5px 4px' : '5px 5px 5px 16px')};
  border: 1px solid #cbd5e1;
  border-radius: 24px;
  background: white;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: #275386;
  }
`;

const InputContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const SearchTextarea = styled.textarea`
  width: 100%;
  padding: 8px 0;
  border: none;
  outline: none;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  background: transparent;
  color: #1e293b;
  box-sizing: border-box;
  resize: none;
  overflow-y: auto;
  display: block; /* inline textarea leaves a baseline gap under it, which pushed it above the send button */
  min-height: 37px;
  max-height: 100px; /* about 4 lines, then it scrolls */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;

  /* Hide scrollbar for cleaner look */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }

  &::placeholder {
    color: #94a3b8;
    font-weight: 400;
  }

  &:disabled {
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const MicButton = styled.button`
  background: ${props => props.$recording
    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    : 'linear-gradient(135deg, #3b74b8 0%, #275386 55%, #1b3c66 100%)'};
  color: white;
  border: none;
  padding: 14px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  box-shadow: ${props => props.$recording
    ? '0 4px 12px rgba(239, 68, 68, 0.3)'
    : '0 4px 12px rgba(39, 83, 134, 0.3)'};
  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
    padding: 12px;
  }
  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    padding: 10px;
  }
  @media (max-width: 320px) {
    width: 40px;
    height: 40px;
    padding: 8px;
  }
  svg {
    width: 20px;
    height: 20px;
  }
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: ${props => props.$recording
      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
      : 'linear-gradient(135deg, #2f66a6 0%, #1f4470 55%, #163353 100%)'};
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  svg.spin {
    animation: spin 1s linear infinite;
  }
`;

// Sits inside the pill: grey until there is text to send, then navy
const SendButton = styled.button`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #275386;
  color: white;
  transition: background 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: #1f4470;
  }

  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

// Live chat only: send a file to the agent
const AttachButton = styled.button`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  color: #64748b;
  transition: color 0.2s ease, background 0.2s ease;

  svg {
    width: 19px;
    height: 19px;
  }

  &:hover:not(:disabled) {
    color: #275386;
    background: #f1f5f9;
  }

  &:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const HintText = styled.div`
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 10px;
  color: #94a3b8;
  font-weight: 400;
  
  @media (max-width: 480px) {
    font-size: 9px;
    bottom: -18px;
  }
`;

const InputContainerComponent = forwardRef(({ 
  value, 
  onChange, 
  onKeyPress, 
  onSend, 
  disabled, 
  placeholder,
  onTranscriptionResult,
  clientId,
  // Live chat only (onAddFiles set): files chosen to send, shown above the box with the text as caption
  onAddFiles,
  stagedFiles = [],
  onRemoveFile,
  attachError
}, ref) => {
  const hasValue = value && value.trim().length > 0;
  const canSend = hasValue || stagedFiles.length > 0;
  const fileInputRef = useRef(null);

  const handleFileChosen = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';  // choosing the same file again still fires
    if (files.length && onAddFiles) onAddFiles(files);
  };
  const chooseFiles = () => fileInputRef.current?.click();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingLimitTimeoutRef = useRef(null);

  // Max recording duration in seconds (auto-stop if user forgets to stop)
  const RECORDING_MAX_DURATION_SEC = 30;

  // Auto-resize textarea based on content
  useEffect(() => {
    if (ref?.current) {
      const textarea = ref.current;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 100)}px`;
    }
  }, [value, ref]);

  // Clear recording limit timeout on unmount
  useEffect(() => {
    return () => {
      if (recordingLimitTimeoutRef.current) {
        clearTimeout(recordingLimitTimeoutRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start(200);
      mediaRecorderRef.current = { recorder, stream };
      setIsRecording(true);

      // Auto-stop after limit so recording doesn't run indefinitely
      if (recordingLimitTimeoutRef.current) clearTimeout(recordingLimitTimeoutRef.current);
      recordingLimitTimeoutRef.current = setTimeout(() => {
        recordingLimitTimeoutRef.current = null;
        stopRecordingAndTranscribe();
      }, RECORDING_MAX_DURATION_SEC * 1000);
    } catch (err) {
      console.error('Microphone access failed:', err);
    }
  };

  const stopRecordingAndTranscribe = async () => {
    if (recordingLimitTimeoutRef.current) {
      clearTimeout(recordingLimitTimeoutRef.current);
      recordingLimitTimeoutRef.current = null;
    }
    const mr = mediaRecorderRef.current;
    if (!mr?.recorder || mr.recorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }
    mr.recorder.stop();
    mediaRecorderRef.current = null;
    setIsRecording(false);

    const chunks = chunksRef.current;
    if (chunks.length === 0 || !onTranscriptionResult) return;

    const blob = new Blob(chunks, { type: 'audio/webm' });
    const { REACT_APP_BACKEND_BOT_URL, REACT_APP_AUTH_TOKEN } = process.env;
    if (!REACT_APP_BACKEND_BOT_URL) {
      console.error('REACT_APP_BACKEND_BOT_URL not set');
      return;
    }

    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'audio.webm');
      formData.append('client_id', (clientId != null && clientId !== '') ? String(clientId) : '0');
      const response = await fetch(`${REACT_APP_BACKEND_BOT_URL}/chat/voice-to-text`, {
        method: 'POST',
        headers: { Authorization: REACT_APP_AUTH_TOKEN || '' },
        body: formData,
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Transcription failed');
      }
      const data = await response.json();
      const text = (data?.text || '').trim();
      if (text) onTranscriptionResult(text);
    } catch (err) {
      console.error('Voice-to-text error:', err);
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecordingAndTranscribe();
    } else {
      startRecording();
    }
  };

  // Enhanced key press handler for multi-line support
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        return;
      } else {
        e.preventDefault();
        if (onKeyPress) {
          onKeyPress(e);
        }
      }
    }
  };

  const micDisabled = disabled || isTranscribing;

  return (
    <Container>
      {onAddFiles && (
        <AttachmentTray
          files={stagedFiles}
          onRemove={onRemoveFile}
          onAddMore={chooseFiles}
          error={attachError}
          disabled={disabled}
        />
      )}
      <InputWrapper $withAttach={Boolean(onAddFiles)}>
        {onAddFiles && (
          <>
            <AttachButton
              type="button"
              onClick={chooseFiles}
              disabled={disabled || stagedFiles.length >= MAX_FILES_PER_SEND}
              title={`Send files: images, PDFs or Office documents, up to 10 MB each, ${MAX_FILES_PER_SEND} at a time`}
              aria-label="Send files"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M21 11.5l-8.6 8.6a5.5 5.5 0 0 1-7.8-7.8l8.6-8.6a3.7 3.7 0 0 1 5.2 5.2l-8.6 8.6a1.8 1.8 0 0 1-2.6-2.6l8-8"
                  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </AttachButton>
            <HiddenFileInput ref={fileInputRef} type="file" multiple accept={ATTACHMENT_ACCEPT} onChange={handleFileChosen} />
          </>
        )}
        <InputContainer>
          <SearchTextarea
            ref={ref}
            value={value}
            onChange={onChange}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
          />
        </InputContainer>

        {/* <MicButton
          type="button"
          onClick={handleMicClick}
          disabled={micDisabled}
          $recording={isRecording}
          title={isTranscribing ? 'Transcribing...' : isRecording ? 'Stop recording' : 'Speak in your preferred language'}
        >
          {isTranscribing ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="spin">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
            </svg>
          ) : isRecording ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          )}
        </MicButton> */}
        
        <SendButton 
          onClick={onSend}
          disabled={disabled || !canSend}
          title={stagedFiles.length ? 'Send files' : 'Send Message'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </SendButton>
      </InputWrapper>
    </Container>
  );
});

InputContainerComponent.displayName = 'InputContainer';

export default InputContainerComponent; 